import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import {
  getAnalyticsViewers, getAnalyticsCreators,
  getAnalyticsUserWatches, getAnalyticsCreatorViewers
} from '../api';
import { Users, BarChart2, Eye, Clock, Film, ChevronRight, X, Search, ArrowLeft } from 'lucide-react';

// Formater les secondes en "Xh Ym"
function fmtTime(seconds) {
  if (!seconds || seconds === 0) return '0 min';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

// Badge couleur rôle
const ROLE_COLORS = {
  superadmin: 'bg-purple-500/20 text-purple-400',
  creator: 'bg-blue-500/20 text-blue-400',
  viewer: 'bg-muted text-muted-foreground',
};
const ROLE_LABELS = { superadmin: 'Superadmin', creator: 'Créateur', viewer: 'Spectateur' };

// ============================================================
// PANNEAU DÉTAIL — Visionnage d'un utilisateur
// ============================================================
function UserWatchDetail({ userId, username, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAnalyticsUserWatches(userId)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-end">
      <div className="w-full max-w-2xl h-full bg-card border-l border-border overflow-y-auto flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-border sticky top-0 bg-card z-10">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold">Détail visionnage</h2>
            <p className="text-sm text-muted-foreground">{username}</p>
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Chargement...
          </div>
        )}

        {!loading && !data && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Impossible de charger les données
          </div>
        )}

        {!loading && data && (
          <div className="p-5 space-y-6">
            {/* Résumé */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-primary">{fmtTime(data.total_watch_seconds)}</p>
                <p className="text-xs text-muted-foreground mt-1">Temps total visionné</p>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold">{data.videos_watched?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Vidéos regardées</p>
              </div>
            </div>

            {/* Par créateur */}
            {data.by_creator?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Temps par créateur
                </h3>
                <div className="space-y-2">
                  {data.by_creator.map((c, i) => (
                    <div key={c.creator_id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground w-5">{i + 1}</span>
                      <span className="flex-1 font-medium text-sm">{c.username}</span>
                      <span className="text-sm text-primary font-semibold">{fmtTime(c.seconds)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vidéos regardées */}
            {data.videos_watched?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Film className="w-4 h-4 text-primary" />
                  Vidéos regardées ({data.videos_watched.length})
                </h3>
                <div className="space-y-2">
                  {data.videos_watched.map(v => (
                    <div key={v.video_id} className="p-3 bg-muted rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{v.title}</p>
                          <p className="text-xs text-muted-foreground">par {v.creator_username}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-primary">{fmtTime(v.watch_seconds)}</p>
                          <p className="text-xs text-muted-foreground">{v.sessions} session{v.sessions > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {v.last_seen && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Vu la dernière fois le {new Date(v.last_seen).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.videos_watched?.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Cet utilisateur n'a pas encore regardé de vidéos</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// PANNEAU DÉTAIL — Spectateurs d'un créateur
// ============================================================
function CreatorViewersDetail({ creatorId, creatorUsername, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAnalyticsCreatorViewers(creatorId)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [creatorId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-end">
      <div className="w-full max-w-2xl h-full bg-card border-l border-border overflow-y-auto flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 p-5 border-b border-border sticky top-0 bg-card z-10">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold">Spectateurs de {creatorUsername}</h2>
            <p className="text-sm text-muted-foreground">Analyse de l'audience</p>
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">Chargement...</div>
        )}
        {!loading && !data && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">Erreur de chargement</div>
        )}

        {!loading && data && (
          <div className="p-5 space-y-6">
            {/* Résumé */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-primary">{fmtTime(data.total_watch_seconds)}</p>
                <p className="text-xs text-muted-foreground mt-1">Watch time total</p>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold">{data.viewers?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Spectateurs uniques</p>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <p className="text-2xl font-bold">{data.videos?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Vidéos publiées</p>
              </div>
            </div>

            {/* Top vidéos */}
            {data.videos?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Film className="w-4 h-4 text-primary" />
                  Performance des vidéos
                </h3>
                <div className="space-y-2">
                  {data.videos.map((v, i) => (
                    <div key={v.video_id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground w-5 shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{v.title}</p>
                        <p className="text-xs text-muted-foreground">{v.unique_viewers} spectateur{v.unique_viewers > 1 ? 's' : ''} unique{v.unique_viewers > 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0">{fmtTime(v.total_watch_seconds)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top spectateurs */}
            {data.viewers?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Top spectateurs ({data.viewers.length})
                </h3>
                <div className="space-y-2">
                  {data.viewers.map((v, i) => (
                    <div key={v.user_id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground w-5 shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{v.username}</p>
                        <p className="text-xs text-muted-foreground">{v.distinct_videos} vidéo{v.distinct_videos > 1 ? 's' : ''} · {v.sessions} session{v.sessions > 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0">{fmtTime(v.total_watch_seconds)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!data.viewers || data.viewers.length === 0) && (
              <div className="text-center text-muted-foreground py-8">
                <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Aucun visionnage enregistré pour ce créateur</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ONGLET SPECTATEURS
// ============================================================
function ViewersTab() {
  const [viewers, setViewers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAnalyticsViewers({ search: search || undefined, limit: LIMIT, skip });
      setViewers(r.data.viewers || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, skip]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          Rechercher
        </button>
      </form>

      {/* Stats résumé */}
      <div className="text-sm text-muted-foreground">
        {total} utilisateur{total > 1 ? 's' : ''} au total
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Utilisateur</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Rôle</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center justify-end gap-1"><Clock className="w-3.5 h-3.5" /> Watch time</span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center justify-end gap-1"><Film className="w-3.5 h-3.5" /> Vidéos</span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Sessions</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Chargement...</td></tr>
            ) : viewers.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Aucun utilisateur trouvé</td></tr>
            ) : viewers.map(v => (
              <tr key={v.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium">{v.username}</p>
                  <p className="text-xs text-muted-foreground">{v.email}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[v.role] || ROLE_COLORS.viewer}`}>
                    {ROLE_LABELS[v.role] || v.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-primary">
                  {fmtTime(v.total_watch_seconds)}
                </td>
                <td className="px-4 py-3 text-right">{v.distinct_videos}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">{v.sessions}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelectedUser(v)}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Détail <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between">
          <button
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - LIMIT))}
            className="px-4 py-2 rounded-lg bg-muted text-sm disabled:opacity-40 hover:bg-muted/70 transition-colors"
          >
            ← Précédent
          </button>
          <span className="text-sm text-muted-foreground">
            {skip + 1}–{Math.min(skip + LIMIT, total)} sur {total}
          </span>
          <button
            disabled={skip + LIMIT >= total}
            onClick={() => setSkip(skip + LIMIT)}
            className="px-4 py-2 rounded-lg bg-muted text-sm disabled:opacity-40 hover:bg-muted/70 transition-colors"
          >
            Suivant →
          </button>
        </div>
      )}

      {/* Detail Panel */}
      {selectedUser && (
        <UserWatchDetail
          userId={selectedUser.id}
          username={selectedUser.username}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// ONGLET CRÉATEURS
// ============================================================
function CreatorsTab() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAnalyticsCreators({ search: search || undefined, limit: LIMIT, skip });
      setCreators(r.data.creators || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, skip]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher un créateur..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          Rechercher
        </button>
      </form>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Créateur</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center justify-end gap-1"><Film className="w-3.5 h-3.5" /> Vidéos</span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center justify-end gap-1"><Eye className="w-3.5 h-3.5" /> Vues</span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center justify-end gap-1"><Clock className="w-3.5 h-3.5" /> Watch time</span>
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Likes</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Chargement...</td></tr>
            ) : creators.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Aucun créateur trouvé</td></tr>
            ) : creators.map(c => (
              <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium">{c.username}</p>
                  <p className="text-xs text-muted-foreground">{c.email}</p>
                </td>
                <td className="px-4 py-3 text-right">{c.video_count}</td>
                <td className="px-4 py-3 text-right">{(c.total_views || 0).toLocaleString('fr-FR')}</td>
                <td className="px-4 py-3 text-right font-semibold text-primary">
                  {fmtTime(c.total_watch_seconds)}
                </td>
                <td className="px-4 py-3 text-right text-muted-foreground">
                  {(c.total_likes || 0).toLocaleString('fr-FR')}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelectedCreator(c)}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Audience <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {creators.length === LIMIT && (
        <div className="flex items-center justify-end">
          <button
            disabled={skip + LIMIT > creators.length + skip}
            onClick={() => setSkip(skip + LIMIT)}
            className="px-4 py-2 rounded-lg bg-muted text-sm hover:bg-muted/70 transition-colors"
          >
            Suivant →
          </button>
        </div>
      )}

      {selectedCreator && (
        <CreatorViewersDetail
          creatorId={selectedCreator.id}
          creatorUsername={selectedCreator.username}
          onClose={() => setSelectedCreator(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// PAGE PRINCIPALE
// ============================================================
const TABS = [
  { id: 'viewers', label: 'Tous les utilisateurs', icon: Users },
  { id: 'creators', label: 'Tous les créateurs', icon: BarChart2 },
];

export const AdminAnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState('viewers');

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-primary" />
            Analytics & Visionnage
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Suivez qui regarde quoi, combien de temps et l'audience de chaque créateur.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'viewers' && <ViewersTab />}
        {activeTab === 'creators' && <CreatorsTab />}
      </div>
    </AdminLayout>
  );
};
